import React, { useCallback, useMemo } from 'react';
import {
    Box,
    Button,
    Chip,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
    Checkbox,
    Menu,
    MenuItem,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArchive, faTrash, faTag, faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
import { TableVirtuoso } from 'react-virtuoso';
import type { TableVirtuosoHandle } from 'react-virtuoso';

import { WorkItem, showsScamMark } from '../api/workItems';
import { DomainConfig } from '../api/domains';
import { isRetiredMode } from '../modes/ModeLifecycleActions';
import { getWorkItemCardClasses } from '../utils/workItemStyling';
import { getWorkItemDisplayTitle, getWorkItemStateLabel, getWorkItemTypeLabel } from '../utils/workItemDisplay';
import ScamMark from './ScamMark';
import { isSourceHomeItem } from '../utils/sourceFamily';
import { calendarYmdInZone, effectiveUserTimeZone } from '../utils/userTime';
import { getEnrichmentConfidence, isLowConfidenceEnrichment } from '../utils/enrichmentConfidence';
import { ViewKey } from './WorkItemsView.constants';
import WorkItemsEmptyState, { WorkItemsEmptyVariant } from './WorkItemsEmptyState';
import WorkItemsBulkActionBar from './WorkItemsBulkActionBar';
import { WorkItemsTableSkeleton } from './WorkItemsTableSkeleton';
import { useTableColumns } from '../hooks/useTableColumns';

export type TableActions = {
    onOpenItem: (item: WorkItem, activator?: HTMLElement | null) => void;
    onQuickDone: (item: WorkItem, e: React.MouseEvent) => void;
    onQuickArchive: (item: WorkItem, e: React.MouseEvent) => void;
    onQuickSnooze: (item: WorkItem, days: number, e: React.MouseEvent) => void;
    onBulkDone: (ids: string[]) => void;
    onBulkArchive: (ids: string[]) => void;
    onBulkSnooze: (ids: string[]) => void;
    onReclassify: (item: WorkItem, domain: string) => void;
};

export type TableState = {
    focusedId: string | null;
    /** Row whose drawer is open. Soft teal glow, separate from checkbox selection. */
    openDrawerId?: string | null;
    selectedIds: Set<string>;
    onRowClick: (id: string, e: React.MouseEvent) => void;
    onRowDoubleClick: (id: string, e: React.MouseEvent) => void;
    onCheckboxClick: (id: string, e: React.MouseEvent) => void;
    onClearSelection: () => void;
};

type WorkItemsTableProps = {
    /** Profile timezone — due urgency borders follow calendar day in this zone */
    userTimeZone?: string | null;
    items: WorkItem[];
    loading: boolean;
    isInitialLoading: boolean;
    errorMessage: string | null;
    onRetry: () => void;
    onLoadMore: () => void;
    domainConfigs: DomainConfig[];
    currentView: ViewKey;
    actions: TableActions;
    state: TableState;
    activeFilters: { id: string; label: string; active: boolean; clear: () => void }[];
    onResetFilters: () => void;
    emptyStateVariant: WorkItemsEmptyVariant;
    mailboxConnected?: boolean;
    onEmptyImportClick?: () => void;
    onEmptyConnectMailClick?: () => void;
    virtuosoRef: React.RefObject<TableVirtuosoHandle | null>;
    archiveAction?: 'archive' | 'delete';
};

const getTitle = (item: WorkItem) => getWorkItemDisplayTitle(item);

const WorkItemsTable: React.FC<WorkItemsTableProps> = ({
    userTimeZone,
    items,
    loading,
    isInitialLoading,
    errorMessage,
    onRetry,
    onLoadMore,
    domainConfigs,
    currentView,
    actions,
    state,
    activeFilters,
    onResetFilters,
    emptyStateVariant,
    mailboxConnected,
    onEmptyImportClick,
    onEmptyConnectMailClick,
    virtuosoRef,
    archiveAction = 'archive',
}) => {
    const [reclassifyAnchorEl, setReclassifyAnchorEl] = React.useState<null | HTMLElement>(null);
    const [reclassifyItem, setReclassifyItem] = React.useState<null | WorkItem>(null);

    const handleReclassifyClick = (event: React.MouseEvent<HTMLElement>, item: WorkItem) => {
        event.stopPropagation();
        setReclassifyAnchorEl(event.currentTarget);
        setReclassifyItem(item);
    };

    const handleReclassifyClose = () => {
        setReclassifyAnchorEl(null);
        setReclassifyItem(null);
    };

    const handleReclassifySelect = (domainKey: string) => {
        if (reclassifyItem) {
            actions.onReclassify(reclassifyItem, domainKey);
        }
        handleReclassifyClose();
    };

    const archiveActionLabel = archiveAction === 'delete' ? 'Delete' : 'Archive';
    const archiveActionColor = archiveAction === 'delete' ? '#ef4444' : 'orange';
    const archiveActionBg = archiveAction === 'delete' ? 'rgba(239, 68, 68, 0.1)' : 'rgba(255, 165, 0, 0.1)';
    const todayYmd = calendarYmdInZone(new Date(), effectiveUserTimeZone(userTimeZone));
    const workItemStylingOptions = useMemo(
        () => ({ userTimeZone, todayYmd }),
        [userTimeZone, todayYmd],
    );

    const { checkboxWidth, columns } = useTableColumns(currentView);

    const renderColGroup = useCallback(() => (
        <colgroup>
            <col style={{ width: checkboxWidth }} />
            {columns.map((col) => (
                <col
                    key={`col-${col.key}`}
                    style={{ width: col.width }}
                />
            ))}
        </colgroup>
    ), [checkboxWidth, columns]);

    const virtuosoComponents = useMemo(() => ({
        Scroller: React.forwardRef((props: any, ref) => (
            <TableContainer
                component={Paper}
                {...props}
                ref={ref}
                elevation={0}
                sx={{ bgcolor: 'transparent', borderRadius: 2, height: '100%', ...props.style }}
            />
        )),
        Table: (props: any) => (
            <Table
                {...props}
                size="small"
                sx={{
                    width: '100%',
                    tableLayout: 'fixed',
                    '& .MuiTableCell-root': { px: 1.25 },
                    '& .MuiTableCell-paddingCheckbox': { px: 0.75 },
                }}
            >
                {renderColGroup()}
                {props.children}
            </Table>
        ),
        TableHead: TableHead,
        TableBody: React.forwardRef((props: any, ref) => <TableBody {...props} ref={ref} />),
        TableRow: ({ context, item, ...props }: any) => {
            const rowIndex = typeof props['data-index'] === 'number' ? props['data-index'] : 0;
            const styling = getWorkItemCardClasses(item, domainConfigs, workItemStylingOptions);
            const isFocused = context.focusedId === item.id;
            const isSelected = context.selectedIds.has(item.id);
            const drawerOpenOnRow = context.openDrawerId != null && context.openDrawerId === item.id;
            const isDone = item.state === 'DONE';
            const isArchived = item.state === 'ARCHIVED';
            const isResolved = isDone || isArchived;

            const titleText = getTitle(item);
            const itemsCount = context.itemsCount ?? 0;
            const a11yBits = [
                isFocused ? 'focused' : null,
                isSelected ? 'selected' : null,
            ].filter(Boolean);
            const a11ySuffix = a11yBits.length ? `, ${a11yBits.join(', ')}` : '';

            return (
                <TableRow
                    {...props}
                    data-testid="WB_ROW"
                    data-item-id={item.id}
                    data-drawer-open={drawerOpenOnRow ? 'true' : undefined}
                    tabIndex={-1}
                    aria-selected={isSelected}
                    aria-rowindex={rowIndex + 1}
                    aria-posinset={rowIndex + 1}
                    aria-setsize={itemsCount}
                    aria-label={`${titleText}, row ${rowIndex + 1} of ${itemsCount}${a11ySuffix}`}
                    onClick={(e: React.MouseEvent) => context.handleItemClick(item.id, e)}
                    onDoubleClick={(e: React.MouseEvent) => context.handleItemDoubleClick(item.id, e)}
                    className="group"
                    sx={{
                        cursor: 'pointer',
                        // Resolved items (Done/Archived) get a faded appearance
                        opacity: isResolved ? 0.5 : 1,
                        bgcolor: isSelected
                            ? 'rgba(99, 102, 241, 0.08)'
                            : isFocused
                                ? 'var(--color-bg-card-hover)'
                                : 'transparent',
                        // Clean left border - domain color, thicker when focused
                        borderLeft: `4px solid ${isResolved ? 'var(--color-border)' : styling.domain.color}`,
                        // Subtle right border for urgency
                        borderRight: `2px solid ${isResolved ? 'transparent' : styling.urgency.color}`,
                        // Hover restores full opacity for interaction
                        '&:hover': {
                            bgcolor: 'var(--color-bg-card-hover)',
                            opacity: isResolved ? 0.75 : 1,
                        },
                        // Focus ring - simple and performant
                        outline: drawerOpenOnRow
                            ? '2px solid rgba(var(--color-accent-rgb), 0.55)'
                            : isFocused
                                ? `2px solid ${styling.domain.color}`
                                : 'none',
                        outlineOffset: '-2px',
                        boxShadow: drawerOpenOnRow ? 'inset 0 0 0 1px rgba(var(--color-accent-rgb), 0.35)' : undefined,
                        // Action buttons visibility
                        '& .row-actions': {
                            opacity: isFocused || isSelected ? 1 : 0,
                        },
                        '&:hover .row-actions': {
                            opacity: 1,
                        }
                    }}
                />
            );
        },
    }), [domainConfigs, renderColGroup, workItemStylingOptions]);

    return (
        <Box className="glass-card p-1 sm:p-2 bg-theme-secondary border border-theme rounded-[1.5rem] sm:rounded-[2rem] overflow-hidden">
            {errorMessage && (
                <Box
                    sx={{
                        mb: 2,
                        px: 2,
                        py: 1.5,
                        borderRadius: 2,
                        border: '1px solid var(--color-border)',
                        bgcolor: 'var(--color-bg-card)',
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 2,
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    <Typography variant="body2" sx={{ color: 'var(--color-text-secondary)', fontWeight: 600 }}>
                        {errorMessage}
                    </Typography>
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={onRetry}
                        sx={{
                            borderColor: 'var(--color-border)',
                            color: 'var(--color-text-secondary)',
                            textTransform: 'none',
                            borderRadius: '12px',
                            '&:hover': { borderColor: 'var(--color-accent)', bgcolor: 'var(--color-bg-card-hover)' },
                        }}
                    >
                        Retry
                    </Button>
                </Box>
            )}
            {isInitialLoading ? (
                <TableContainer component={Paper} elevation={0} sx={{ bgcolor: 'transparent', borderRadius: 2 }}>
                    <Table
                        size="small"
                        sx={{
                            width: '100%',
                            tableLayout: 'fixed',
                            '& .MuiTableCell-root': { px: 1.25 },
                            '& .MuiTableCell-paddingCheckbox': { px: 0.75 },
                        }}
                    >
                        {renderColGroup()}
                        <TableHead>
                            <TableRow sx={{ bgcolor: 'var(--color-bg-card)' }}>
                                <TableCell padding="checkbox" sx={{ bgcolor: 'var(--color-bg-card)', borderBottom: '1px solid var(--color-border)' }} />
                                {columns.map((col) => (
                                    <TableCell
                                        key={col.key}
                                        scope="col"
                                        padding="normal"
                                        sx={{
                                            color: 'var(--color-text-secondary)',
                                            fontSize: '10px',
                                            fontWeight: 'bold',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.1em',
                                            bgcolor: 'var(--color-bg-card)',
                                            borderBottom: '1px solid var(--color-border)',
                                            // Handle title specific align if needed, usually left is fine
                                        }}
                                    >
                                        {col.label}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            <WorkItemsTableSkeleton currentView={currentView} />
                        </TableBody>
                    </Table>
                </TableContainer>
            ) : (
                <Box sx={{ height: 'calc(100vh - 320px)', minHeight: 400, maxHeight: 900, width: '100%', position: 'relative' }}>
                    <TableVirtuoso
                        ref={virtuosoRef}
                        data={items}
                        endReached={onLoadMore}
                        computeItemKey={(_, item) => item.id}
                        context={{
                            focusedId: state.focusedId,
                            openDrawerId: state.openDrawerId ?? null,
                            selectedIds: state.selectedIds,
                            handleItemClick: state.onRowClick,
                            handleItemDoubleClick: state.onRowDoubleClick,
                            handleCheckboxClick: state.onCheckboxClick,
                            itemsCount: items.length,
                        }}
                        components={virtuosoComponents}
                        fixedHeaderContent={() => (
                            <TableRow sx={{ bgcolor: 'var(--color-bg-card)' }}>
                                <TableCell padding="checkbox" sx={{ bgcolor: 'var(--color-bg-card)', borderBottom: '1px solid var(--color-border)' }} />
                                {columns.map((col) => (
                                    <TableCell
                                        key={col.key}
                                        scope="col"
                                        padding="normal"
                                        sx={{
                                            color: 'var(--color-text-secondary)',
                                            fontSize: '10px',
                                            fontWeight: 'bold',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.1em',
                                            bgcolor: 'var(--color-bg-card)',
                                            borderBottom: '1px solid var(--color-border)'
                                        }}
                                    >
                                        {col.label}
                                    </TableCell>
                                ))}
                            </TableRow>
                        )}
                        itemContent={(index, item) => {
                            const styling = getWorkItemCardClasses(item, domainConfigs, workItemStylingOptions);
                            return (
                                <>
                                    <TableCell padding="checkbox" sx={{ borderBottom: '1px solid var(--color-border)' }}>
                                        <Checkbox
                                            checked={state.selectedIds.has(item.id)}
                                            onClick={(e) => state.onCheckboxClick(item.id, e)}
                                            size="small"
                                            sx={{ color: 'var(--color-text-muted)', '&.Mui-checked': { color: '#6366f1' } }}
                                        />
                                    </TableCell>
                                    {columns.map((col) => {
                                        const isItemDone = item.state === 'DONE';
                                        const isItemArchived = item.state === 'ARCHIVED';
                                        const isItemResolved = isItemDone || isItemArchived;

                                        switch (col.key) {
                                            case 'title':
                                                return (
                                                    <TableCell key={col.key} sx={{ borderBottom: '1px solid var(--color-border)', py: 2 }}>
                                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, minWidth: 0 }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                {isItemDone && (
                                                                    <CheckCircleIcon sx={{ fontSize: 14, color: '#22c55e', flexShrink: 0 }} />
                                                                )}
                                                                {isItemArchived && (
                                                                    <FontAwesomeIcon icon={faArchive} style={{ fontSize: 11, color: 'var(--color-text-muted)', flexShrink: 0 }} />
                                                                )}
                                                                <Typography variant="body2" sx={{
                                                                    color: isItemResolved ? 'var(--color-text-muted)' : 'var(--color-text-primary)',
                                                                    fontWeight: isItemResolved ? 400 : 700,
                                                                    letterSpacing: '-0.01em',
                                                                    textDecoration: isItemDone ? 'line-through' : 'none',
                                                                    fontStyle: isItemArchived ? 'italic' : 'normal',
                                                                }}>
                                                                    {getTitle(item)}
                                                                </Typography>
                                                                {showsScamMark(item.safety) && (
                                                                    <ScamMark data-testid="WB_ROW_RISK_MARK" />
                                                                )}
                                                            </Box>
                                                            <Box
                                                                component="span"
                                                                title={styling.domain.label}
                                                                sx={{
                                                                    display: 'inline-flex',
                                                                    alignItems: 'center',
                                                                    gap: 0.75,
                                                                    px: 1,
                                                                    py: 0.25,
                                                                    borderRadius: 99,
                                                                    bgcolor: 'var(--color-bg-tertiary)',
                                                                    border: '1px solid var(--color-border)',
                                                                    color: 'var(--color-text-secondary)',
                                                                    fontSize: '10px',
                                                                    fontWeight: 900,
                                                                    width: 'fit-content',
                                                                    maxWidth: 320,
                                                                }}
                                                            >
                                                                <Box sx={{ width: 6, height: 6, borderRadius: 99, bgcolor: styling.domain.color, flexShrink: 0 }} />
                                                                <span aria-hidden>{styling.domain.icon}</span>
                                                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{styling.domain.label}</span>
                                                            </Box>
                                                            {item.summary && (
                                                                <Typography variant="caption" sx={{ color: 'var(--color-text-secondary)', display: 'block', lineHeight: 1.2 }}>
                                                                    {item.summary.length > 80 ? item.summary.substring(0, 80) + '...' : item.summary}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    </TableCell>
                                                );
                                            case 'type': {
                                                const typeLabel = getWorkItemTypeLabel(item.type) ?? item.type ?? 'Unknown';
                                                return (
                                                    <TableCell key={col.key} sx={{ borderBottom: '1px solid var(--color-border)' }}>
                                                        <Typography variant="caption" sx={{ color: 'var(--color-text-muted)', fontWeight: 700 }}>{typeLabel}</Typography>
                                                    </TableCell>
                                                );
                                            }
                                            case 'state': {
                                                const stateLabel = getWorkItemStateLabel(item.state) ?? item.state ?? 'Unknown';
                                                const decoratedStateLabel = isItemDone
                                                    ? `✓ ${stateLabel}`
                                                    : isItemArchived
                                                        ? `📦 ${stateLabel}`
                                                        : stateLabel;
                                                return (
                                                    <TableCell key={col.key} sx={{ borderBottom: '1px solid var(--color-border)' }}>
                                                        <Chip
                                                            size="small"
                                                            label={decoratedStateLabel}
                                                            variant={isItemResolved ? 'filled' : 'outlined'}
                                                            sx={{
                                                                fontSize: '9px',
                                                                height: 18,
                                                                fontWeight: isItemResolved ? 700 : 400,
                                                                color: isItemDone
                                                                    ? '#22c55e'
                                                                    : isItemArchived
                                                                        ? 'var(--color-text-muted)'
                                                                        : 'var(--color-text-secondary)',
                                                                bgcolor: isItemDone
                                                                    ? 'rgba(34, 197, 94, 0.1)'
                                                                    : isItemArchived
                                                                        ? 'rgba(148, 163, 184, 0.08)'
                                                                        : 'transparent',
                                                                borderColor: isItemDone
                                                                    ? 'rgba(34, 197, 94, 0.3)'
                                                                    : isItemArchived
                                                                        ? 'rgba(148, 163, 184, 0.2)'
                                                                        : 'var(--color-border)',
                                                            }}
                                                        />
                                                    </TableCell>
                                                );
                                            }
                                            case 'source':
                                                return (
                                                    <TableCell key={col.key} sx={{ borderBottom: '1px solid var(--color-border)' }}>
                                                        <Typography variant="caption" sx={{ color: 'var(--color-text-secondary)' }}>{item.source || 'Email'}</Typography>
                                                    </TableCell>
                                                );
                                            case 'statusCompany':
                                                return (
                                                    <TableCell key={col.key} sx={{ borderBottom: '1px solid var(--color-border)' }}>
                                                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                                            <Chip size="small" label={item.status || 'Applied'} sx={{ bgcolor: 'var(--color-bg-card-hover)', color: 'var(--color-text-primary)', border: '1px solid var(--color-border)', fontSize: '10px', height: 20, mb: 0.5 }} />
                                                            <Typography variant="caption" sx={{ color: 'var(--color-text-secondary)', fontSize: '9px' }}>{item.company || '\u2014'}</Typography>
                                                        </Box>
                                                    </TableCell>
                                                );
                                            case 'nextAction':
                                                return (
                                                    <TableCell key={col.key} sx={{ borderBottom: '1px solid var(--color-border)' }}>
                                                        <Typography variant="caption" sx={{ color: item.needs_action ? 'orange' : 'var(--color-text-secondary)', fontWeight: item.needs_action ? 700 : 400 }}>
                                                            {item.needs_action ? 'Action Required' : 'No immediate action'}
                                                        </Typography>
                                                    </TableCell>
                                                );
                                            case 'tags':
                                                return (
                                                    <TableCell key={col.key} sx={{ borderBottom: '1px solid var(--color-border)' }}>
                                                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                                            {(item.tags || []).slice(0, 2).map((tag) => (
                                                                <Chip
                                                                    key={tag}
                                                                    label={tag}
                                                                    size="small"
                                                                    sx={{ fontSize: '8px', height: 16, bgcolor: 'var(--color-bg-card-hover)', color: 'var(--color-text-secondary)', border: '1px solid var(--color-border)' }}
                                                                />
                                                            ))}
                                                        </Box>
                                                    </TableCell>
                                                );
                                            case 'confidence': {
                                                const enrichmentConfidence = getEnrichmentConfidence(item);
                                                const lowConfidence = isLowConfidenceEnrichment(item);
                                                const confidenceLabel = `${Math.round(enrichmentConfidence ?? item.confidence_score ?? 0)}%`;
                                                return (
                                                    <TableCell key={col.key} sx={{ borderBottom: '1px solid var(--color-border)' }}>
                                                        <Tooltip title={lowConfidence ? 'Inferred from email' : 'Confidence score'}>
                                                            <div
                                                                className="px-1.5 py-0.5 rounded bg-indigo-500/10 border border-indigo-500/30 text-[9px] font-black text-indigo-600 w-fit"
                                                                aria-label={lowConfidence ? `Inferred from email, ${confidenceLabel}` : `Confidence ${confidenceLabel}`}
                                                            >
                                                                {confidenceLabel}
                                                            </div>
                                                        </Tooltip>
                                                    </TableCell>
                                                );
                                            }
                                            case 'timeline':
                                                return (
                                                    <TableCell key={col.key} sx={{ borderBottom: '1px solid var(--color-border)' }}>
                                                        <div className="flex flex-col gap-1">
                                                            {item.due_date ? (
                                                                <div className={`text-[10px] font-black tracking-widest uppercase px-2 py-0.5 rounded-md w-fit border ${styling.urgency.urgency === 'overdue'
                                                                    ? 'bg-red-500/10 border-red-500/30 text-red-600'
                                                                    : styling.urgency.urgency === 'today'
                                                                        ? 'bg-orange-500/10 border-orange-500/30 text-orange-600'
                                                                        : 'bg-indigo-500/10 border-indigo-500/30 text-indigo-600'
                                                                    }`}>
                                                                    {styling.urgency.label}
                                                                </div>
                                                            ) : (
                                                                <Typography variant="caption" sx={{ color: 'var(--color-text-muted)', fontSize: '9px' }}>
                                                                    {new Date(item.last_activity_at || item.applied_at || new Date()).toLocaleDateString()}
                                                                </Typography>
                                                            )}
                                                        </div>
                                                    </TableCell>
                                                );
                                            case 'actions':
                                                return (
                                                    <TableCell key={col.key} sx={{ borderBottom: '1px solid var(--color-border)' }}>
                                                        <Box
                                                            className="row-actions flex items-center gap-0.5"
                                                            sx={{
                                                                transition: 'opacity 0.15s ease',
                                                                '& .MuiIconButton-root': { p: 0.5 },
                                                            }}
                                                        >
                                                            <Tooltip title="Open Drawer">
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={(e) => { e.stopPropagation(); actions.onOpenItem(item, e.currentTarget); }}
                                                                    sx={{ color: 'var(--color-accent)', '&:hover': { bgcolor: 'rgba(99, 102, 241, 0.1)' } }}
                                                                >
                                                                    <FontAwesomeIcon icon={faExternalLinkAlt} size="xs" />
                                                                </IconButton>
                                                            </Tooltip>
                                                            {archiveAction === 'archive' && !isSourceHomeItem(item) && (
                                                                <Tooltip title="Instant Done">
                                                                    <IconButton size="small" onClick={(e) => actions.onQuickDone(item, e)} sx={{ color: 'var(--color-text-muted)', '&:hover': { color: '#4caf50', bgcolor: 'rgba(76, 175, 80, 0.1)' } }}>
                                                                        <CheckCircleIcon fontSize="inherit" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            )}
                                                            <Tooltip title="Reclassify">
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={(e) => handleReclassifyClick(e, item)}
                                                                    sx={{ color: 'var(--color-text-muted)', '&:hover': { color: 'var(--color-accent)', bgcolor: 'rgba(99, 102, 241, 0.1)' } }}
                                                                >
                                                                    <FontAwesomeIcon icon={faTag} size="xs" />
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Tooltip title={archiveActionLabel}>
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={(e) => actions.onQuickArchive(item, e)}
                                                                    sx={{ color: 'var(--color-text-muted)', '&:hover': { color: archiveActionColor, bgcolor: archiveActionBg } }}
                                                                >
                                                                    <FontAwesomeIcon icon={archiveAction === 'delete' ? faTrash : faArchive} size="xs" />
                                                                </IconButton>
                                                            </Tooltip>
                                                            {archiveAction === 'archive' && (
                                                                <Tooltip title="Snooze 24h">
                                                                    <IconButton size="small" onClick={(e) => actions.onQuickSnooze(item, 1, e)} sx={{ color: 'var(--color-text-muted)', '&:hover': { color: '#ff9800', bgcolor: 'rgba(255, 152, 0, 0.1)' } }}>
                                                                        <AccessTimeIcon fontSize="inherit" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            )}
                                                        </Box>
                                                    </TableCell>
                                                );
                                            default:
                                                return null;
                                        }
                                    })}
                                </>
                            );
                        }}
                    />

                    <Menu
                        anchorEl={reclassifyAnchorEl}
                        open={Boolean(reclassifyAnchorEl)}
                        onClose={handleReclassifyClose}
                        PaperProps={{
                            sx: {
                                bgcolor: 'var(--color-bg-card)',
                                border: '1px solid var(--color-border)',
                                borderRadius: 2,
                                boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                                minWidth: 160
                            }
                        }}
                    >
                        <Box sx={{ px: 2, py: 1, borderBottom: '1px solid var(--color-border)', mb: 0.5 }}>
                            <Typography variant="caption" sx={{ color: 'var(--color-text-muted)', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                Move to Domain
                            </Typography>
                        </Box>
                        {domainConfigs.filter((config) => !isRetiredMode(config)).map((config) => (
                            <MenuItem
                                key={config.key}
                                onClick={() => handleReclassifySelect(config.key)}
                                sx={{
                                    fontSize: '13px',
                                    py: 1,
                                    color: reclassifyItem?.domain === config.key ? 'var(--color-accent)' : 'var(--color-text-primary)',
                                    fontWeight: reclassifyItem?.domain === config.key ? 700 : 400,
                                    '&:hover': { bgcolor: 'rgba(99, 102, 241, 0.08)' }
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <span style={{ fontSize: '16px' }}>{config.icon}</span>
                                    {config.label}
                                </Box>
                            </MenuItem>
                        ))}
                    </Menu>

                    {loading && items.length > 0 && (
                        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-1.5 rounded-full bg-theme-card border border-theme text-[10px] font-bold uppercase tracking-[0.2em] text-theme-muted shadow-sm">
                            Loading{'\u2026'}
                        </div>
                    )}

                    {!loading && items.length === 0 && !errorMessage && (
                        <WorkItemsEmptyState
                            variant={emptyStateVariant}
                            activeFilters={activeFilters}
                            onResetFilters={onResetFilters}
                            mailboxConnected={mailboxConnected}
                            onImportClick={onEmptyImportClick}
                            onConnectMailClick={onEmptyConnectMailClick}
                        />
                    )}

                    {state.selectedIds.size > 0 && (
                        <WorkItemsBulkActionBar
                            selectedCount={state.selectedIds.size}
                            onDone={archiveAction === 'archive' ? () => actions.onBulkDone(Array.from(state.selectedIds)) : undefined}
                            onSnooze={archiveAction === 'archive' ? () => actions.onBulkSnooze(Array.from(state.selectedIds)) : undefined}
                            onArchive={() => actions.onBulkArchive(Array.from(state.selectedIds))}
                            onClearSelection={state.onClearSelection}
                            archiveLabel={archiveActionLabel}
                            archiveColor={archiveAction === 'delete' ? '#ef4444' : 'var(--color-text-secondary)'}
                        />
                    )}
                </Box>
            )}
        </Box>
    );
};

export default WorkItemsTable;
